import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Database, AlertTriangle, CheckCircle2 } from 'lucide-react'
import {
  clients as clientsApi,
  importFromV1,
  type ImportSource,
} from '../api'
import { useAuth } from '../auth'
import { confirmDestructive } from '../components/destructive'

const ALL_SOURCES: ImportSource[] = ['emails', 'docs', 'attachments']

export default function ImportV1() {
  const auth = useAuth()
  // Imports is platform-admin only (writes against tenant data + the
  // legacy DB). studio_viewer should not see it even though they have
  // Studio access otherwise — the top-nav link already filters by
  // is_superuser / is_staff via the Layout adminOnly flag.
  const isAdmin = auth.status === 'authenticated' && (auth.user.is_staff || auth.user.is_superuser)

  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list(),
    enabled: isAdmin,
  })

  const [clientCode, setClientCode] = useState('')
  const [legacyClientId, setLegacyClientId] = useState<string>('1')
  const [sources, setSources] = useState<ImportSource[]>([...ALL_SOURCES])
  const [limit, setLimit] = useState<string>('50')
  const [offset, setOffset] = useState<string>('0')
  // Date window (CLI flags). Empty = no bound on that side. Accepts
  // YYYY-MM-DD (sent as-is to the backend, which assumes UTC midnight).
  const [since, setSince] = useState<string>('')
  const [until, setUntil] = useState<string>('')

  const importMu = useMutation({
    mutationFn: () =>
      importFromV1({
        client_code: clientCode,
        legacy_client_id: Number(legacyClientId),
        sources,
        limit: Number(limit),
        offset: Number(offset),
        since: since || null,
        until: until || null,
      }),
  })

  function runImport() {
    const window = since || until ? `${since || '−∞'} → ${until || '+∞'}` : 'NO date filter (everything)'
    const message =
      `Run import?\n\n` +
      `  Tenant:  ${clientCode}\n` +
      `  Legacy:  ${legacyClientId}\n` +
      `  Sources: ${sources.join(', ')}\n` +
      `  Window:  ${window}\n` +
      `  Limit:   ${limit}  Offset: ${offset}\n\n` +
      `Idempotent on (client, source_type, source_external_id) — re-runs don't duplicate.`
    if (confirmDestructive(message)) importMu.mutate()
  }

  if (!isAdmin) {
    return (
      <div className="me-scroll p-8 text-[13px] me-text-mute">
        <AlertTriangle size={14} className="mr-1 inline" />
        This page is admin-only.
      </div>
    )
  }

  function toggleSource(s: ImportSource) {
    setSources((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]))
  }

  return (
    <div className="me-scroll mx-auto max-w-3xl overflow-auto p-8">
      <h1 className="flex items-center gap-2 text-[20px] font-semibold me-text">
        <Database size={18} /> Import from v1 (matter_legacy)
      </h1>
      <p className="mt-2 text-[13px] me-text-dim">
        Pull artifacts from the legacy <code className="me-mono">zz_raw_*</code> tables into a v2 tenant.
        Idempotent on <code className="me-mono">(client, source_type, source_external_id)</code>: re-running
        doesn't duplicate, and changed content marks dependent ComputationResults stale (cascade picks them up
        on the next pipeline run). Comments + redlines fold into their parent doc/attachment.
      </p>

      <div className="me-card mt-6 p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Target tenant (v2 Client.code)">
            <select
              value={clientCode}
              onChange={(e) => setClientCode(e.target.value)}
              className="me-input w-full"
            >
              <option value="">choose…</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={c.code}>{c.code} — {c.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Legacy DB client_id (numeric)">
            <input
              type="number"
              value={legacyClientId}
              onChange={(e) => setLegacyClientId(e.target.value)}
              className="me-input w-full"
            />
          </Field>
        </div>

        <Field label="Sources">
          <div className="flex gap-3">
            {ALL_SOURCES.map((s) => (
              <label key={s} className="flex items-center gap-1.5 text-[12.5px] me-text">
                <input
                  type="checkbox"
                  checked={sources.includes(s)}
                  onChange={() => toggleSource(s)}
                  className="cursor-pointer"
                />
                {s}
              </label>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Limit (per source, 1–500)">
            <input
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="me-input w-full"
            />
          </Field>
          <Field label="Offset">
            <input
              type="number"
              min={0}
              value={offset}
              onChange={(e) => setOffset(e.target.value)}
              className="me-input w-full"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Since (date or ISO datetime, ≥)">
            <input
              type="text"
              value={since}
              placeholder="2025-06-01"
              onChange={(e) => setSince(e.target.value)}
              className="me-input w-full"
            />
          </Field>
          <Field label="Until (date or ISO datetime, &lt;)">
            <input
              type="text"
              value={until}
              placeholder="2025-07-01"
              onChange={(e) => setUntil(e.target.value)}
              className="me-input w-full"
            />
          </Field>
        </div>
        <div className="text-[11px] me-text-mute">
          Filter applies to <code className="me-mono">internal_date</code> (emails) and
          <code className="me-mono"> modified_time</code> (docs/attachments). Leave blank for no bound.
        </div>

        <div className="flex justify-end gap-2">
          <button
            disabled={
              !clientCode ||
              !legacyClientId ||
              sources.length === 0 ||
              importMu.isPending
            }
            onClick={runImport}
            className="me-btn me-btn-primary"
          >
            {importMu.isPending ? 'Importing…' : 'Run import…'}
          </button>
        </div>

        {importMu.error && (
          <div className="rounded-sm border border-[color:var(--me-stale)] bg-[color:var(--me-staleBg)] px-2 py-1.5 text-[12px] text-[color:var(--me-stale)]">
            <AlertTriangle size={12} className="mr-1 inline" />
            {(importMu.error as Error).message}
          </div>
        )}
      </div>

      {importMu.data && (
        <div className="me-card mt-6 p-4">
          <div className="flex items-center gap-1.5 text-[14px] font-medium me-text">
            <CheckCircle2 size={14} className="text-[color:var(--me-fresh)]" />
            Done — {importMu.data.client}, legacy id {importMu.data.legacy_client_id}
          </div>
          <table className="mt-3 w-full text-[12px]">
            <thead>
              <tr className="me-mono me-text-mute">
                <th className="text-left font-normal">source</th>
                <th className="text-right font-normal">created</th>
                <th className="text-right font-normal">updated</th>
                <th className="text-right font-normal">skipped</th>
                <th className="text-right font-normal">total</th>
              </tr>
            </thead>
            <tbody className="me-mono">
              {ALL_SOURCES.map((s) => {
                const r = importMu.data!.report[s]
                if (!r) return null
                return (
                  <tr key={s} className="border-t me-border">
                    <td className="py-1">{s}</td>
                    <td className="text-right py-1 text-[color:var(--me-fresh)]">{r.created}</td>
                    <td className="text-right py-1 text-[color:var(--me-stale)]">{r.updated}</td>
                    <td className="text-right py-1 me-text-mute">{r.skipped}</td>
                    <td className="text-right py-1">{r.total}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="mt-3 text-[11px] me-text-mute">
            Re-run with a higher offset to fetch the next batch. The window is global per source, not per-tenant.
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="me-section-h !mb-0.5">{label}</div>
      {children}
    </label>
  )
}
